import { useState } from 'react';

// The RepBuddy agent characters. The four agents render the official character
// art from /public/agents (web-sized copies of the originals in the brand kit:
// Desktop/RepBuddy/LOGO Repbuddy). If an image ever fails to load, we fall back
// to the inline pixel-SVG bots below so a tag never renders empty. The 'you'
// (person) and 'list' (clipboard) pipeline-endpoint glyphs stay SVG - there is
// no character art for them.

const BEZEL  = '#153f27';
const SCREEN = '#06180d';
const STROKE = 'rgba(124, 255, 107, 0.4)';

const FILLS = {
  green: 'var(--accent)',
  amber: 'var(--amber)',
  dim:   'var(--text-muted)',
};

// Face pixels: [x, y, w, h, fillKey?] on a 16×16 grid (screen area x3–12, y5–10).
const FACES = {
  hunter: {
    tone: 'green',
    px: [
      [5, 6, 1, 3], [4, 7, 3, 1],   // left crosshair eye
      [10, 6, 1, 3], [9, 7, 3, 1],  // right crosshair eye
      [6, 9, 4, 1],                 // mouth
    ],
  },
  verifier: {
    tone: 'amber',
    px: [
      [4, 6, 2, 2], [10, 6, 2, 2],                          // eyes
      [5, 9, 1, 1], [6, 10, 1, 1], [7, 9, 1, 1], [8, 8, 1, 1], // check-mark smile
    ],
  },
  analyst: {
    tone: 'green',
    px: [
      [4, 6, 3, 2], [9, 6, 3, 2], [7, 6, 2, 1],             // glasses + bridge
      [5, 9, 1, 2, 'amber'], [7, 8, 1, 3, 'amber'], [9, 9, 1, 2, 'amber'], // bar chart
    ],
  },
  composer: {
    tone: 'amber',
    px: [
      [4, 6, 2, 2],                                // open eye
      [9, 7, 3, 1],                                // wink
      [5, 10, 2, 1], [7, 9, 2, 1], [9, 10, 2, 1], // scribble mouth
    ],
  },
};

function Bot({ face }) {
  const tone = FILLS[face.tone];
  return (
    <>
      <rect x="7" y="1" width="2" height="2" rx="0.5" fill={tone} />
      <rect x="1" y="3" width="14" height="10" rx="2" fill={BEZEL} stroke={STROKE} strokeWidth="0.75" />
      <rect x="3" y="5" width="10" height="6" rx="1" fill={SCREEN} />
      {face.px.map(([x, y, w, h, f], i) => (
        <rect key={i} x={x} y={y} width={w} height={h} fill={f ? FILLS[f] : tone} />
      ))}
    </>
  );
}

function Person() {
  return (
    <>
      <rect x="6" y="2" width="4" height="4" rx="1" fill={FILLS.dim} />
      <rect x="3" y="8" width="10" height="6" rx="2.5" fill={FILLS.dim} />
    </>
  );
}

function Clipboard() {
  return (
    <>
      <rect x="2" y="2" width="12" height="12" rx="2" fill={BEZEL} stroke={STROKE} strokeWidth="0.75" />
      <rect x="4" y="4" width="8" height="8" rx="1" fill={SCREEN} />
      <rect x="5" y="5" width="6" height="1" fill={FILLS.dim} />
      <rect x="5" y="7" width="4" height="1" fill={FILLS.dim} />
      <rect x="6" y="9" width="1" height="1" fill={FILLS.green} />
      <rect x="7" y="10" width="1" height="1" fill={FILLS.green} />
      <rect x="8" y="9" width="1" height="1" fill={FILLS.green} />
      <rect x="9" y="8" width="1" height="1" fill={FILLS.green} />
    </>
  );
}

const CHARACTERS = ['hunter', 'verifier', 'analyst', 'composer'];

export default function AgentAvatar({ agent = 'hunter', size = 20, className = '' }) {
  const [imgOk, setImgOk] = useState(true);

  if (CHARACTERS.includes(agent) && imgOk) {
    return (
      <img
        className={className}
        src={`${process.env.PUBLIC_URL}/agents/${agent}.png`}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
        alt=""
        aria-hidden="true"
        draggable="false"
        onError={() => setImgOk(false)}
      />
    );
  }

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {agent === 'you' ? <Person />
        : agent === 'list' ? <Clipboard />
        : <Bot face={FACES[agent] || FACES.hunter} />}
    </svg>
  );
}
